if (!customElements.get("product-personalizer")) {
  class ProductPersonalizer extends HTMLElement {
    constructor() {
      super();
      this.openButtons = [];
      this.opener = null;
      this._focusTrap = null;

      // Working state, committed to the form only when "apply" is pressed.
      this.state = {
        text: "",
        image: null,
        // An empty family means the popup's own (theme) font.
        font: { name: "", family: "" },
        logo: null, // { name, dataUrl, size }
        logoSize: 30,
        x: 50,
        y: 50,
      };
      this.applied = null;

      this.onOpen = this.onOpen.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onApply = this.onApply.bind(this);
      this.onRemove = this.onRemove.bind(this);
      this.onTextInput = this.onTextInput.bind(this);
      this.onLogoChange = this.onLogoChange.bind(this);
      this.onLogoRemove = this.onLogoRemove.bind(this);
      this.onLogoSizeChange = this.onLogoSizeChange.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onTextKeydown = this.onTextKeydown.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
      // Move the popup to <body> before anything else. Inside the section it is at
      // the mercy of any ancestor with transform/filter/contain, which turns
      // position:fixed into a block laid out inside the product column.
      if (this.parentElement !== document.body) {
        const previous = document.querySelector(
          `body > product-personalizer[id="${this.id}"]`
        );
        if (previous && previous !== this) previous.remove();
        this._relocating = true;
        document.body.appendChild(this);
        this._relocating = false;
        return;
      }

      this.modalId = this.id;
      this.stage = this.querySelector("[data-personalizer-stage]");
      this.image = this.querySelector("[data-personalizer-image]");
      this.textLayer = this.querySelector("[data-personalizer-text]");
      this.field = this.querySelector("[data-personalizer-field]");
      this.counter = this.querySelector("[data-personalizer-counter]");
      this.applyButton = this.querySelector("[data-personalizer-apply]");
      this.overlay = this.querySelector("[data-personalizer-overlay]");
      this.thumbs = Array.from(this.querySelectorAll("[data-personalizer-thumb]"));
      this.textValue = this.querySelector("[data-personalizer-text-value]");
      this.fontButtons = Array.from(this.querySelectorAll("[data-personalizer-font]"));
      this.logoInput = this.querySelector("[data-personalizer-logo]");
      this.logoPreview = this.querySelector("[data-personalizer-logo-preview]");
      this.logoName = this.querySelector("[data-personalizer-logo-name]");
      this.logoRemove = this.querySelector("[data-personalizer-logo-remove]");
      this.logoError = this.querySelector("[data-personalizer-logo-error]");
      this.logoSizeField = this.querySelector("[data-personalizer-logo-size-field]");
      this.logoSizeInput = this.querySelector("[data-personalizer-logo-size]");
      this.logoSizeValue = this.querySelector("[data-personalizer-logo-size-value]");
      this.closeButtons = Array.from(this.querySelectorAll("[data-personalizer-close]"));
      this.maxCharacters = Number.parseInt(this.dataset.maxCharacters || "25", 10) || 25;

      // The engraving is always black.
      this.state.color = this.dataset.defaultColor || "#000000";
      const firstThumb = this.thumbs[0];
      if (firstThumb) this.state.image = this.thumbToImage(firstThumb);
      const firstFont = this.fontButtons[0];
      if (firstFont) {
        this.state.font = {
          name: firstFont.dataset.fontName || "",
          family: firstFont.dataset.personalizerFont || "",
        };
      }

      this.bindTriggers();
      this.closeButtons.forEach((button) => button.addEventListener("click", this.onClose));
      this.applyButton?.addEventListener("click", this.onApply);
      this.field?.addEventListener("input", this.onTextInput);
      this.thumbs.forEach((thumb) =>
        thumb.addEventListener("click", () => this.selectImage(thumb))
      );
      this.logoInput?.addEventListener("change", this.onLogoChange);
      this.logoRemove?.addEventListener("click", this.onLogoRemove);
      this.logoSizeInput?.addEventListener("input", this.onLogoSizeChange);
      this.fontButtons.forEach((button) => {
        // Preview each option in its own typeface. Set from the dataset so a
        // family containing quotes can't break the HTML attribute.
        button.style.fontFamily = button.dataset.personalizerFont || "";
        button.addEventListener("click", () => this.selectFont(button));
      });
      this.textLayer?.addEventListener("pointerdown", this.onPointerDown);
      this.textLayer?.addEventListener("keydown", this.onTextKeydown);
      window.addEventListener("resize", this.onResize);
      document.addEventListener("variant:change", this.onVariantChange);

      this.renderText();
    }

    disconnectedCallback() {
      if (this._relocating) return;
      this.unbindTriggers();
      this.closeButtons.forEach((button) => button.removeEventListener("click", this.onClose));
      window.removeEventListener("resize", this.onResize);
      document.removeEventListener("variant:change", this.onVariantChange);
      document.removeEventListener("pointermove", this.onPointerMove);
      document.removeEventListener("pointerup", this.onPointerUp);
      if (this._focusTrap) this.removeEventListener("keydown", this._focusTrap);
    }

    /* ---------------------------------------------------------------- triggers */

    bindTriggers() {
      this.openButtons = this.modalId
        ? Array.from(
            document.querySelectorAll(
              `[data-personalizer-open][aria-controls="${this.modalId}"]`
            )
          )
        : [];
      this.openButtons.forEach((button) => button.addEventListener("click", this.onOpen));

      this.removeButtons = Array.from(
        document.querySelectorAll("[data-personalizer-remove]")
      ).filter((button) => this.triggerZoneOf(button));
      this.removeButtons.forEach((button) => button.addEventListener("click", this.onRemove));
    }

    unbindTriggers() {
      this.openButtons.forEach((button) => button.removeEventListener("click", this.onOpen));
      this.openButtons = [];
      (this.removeButtons || []).forEach((button) =>
        button.removeEventListener("click", this.onRemove)
      );
      this.removeButtons = [];
    }

    triggerZoneOf(element) {
      return element.closest("[data-personalizer-trigger-zone]");
    }

    onVariantChange() {
      this.unbindTriggers();
      // Wait one frame for the DOM replacement to complete.
      requestAnimationFrame(() => {
        this.bindTriggers();
        this.renderSummary();
      });
    }

    /* ------------------------------------------------------------------- modal */

    onOpen(event) {
      event.preventDefault();
      this.opener = event.currentTarget;

      // Reopening always starts from what is currently applied.
      if (this.applied) {
        this.state = { ...this.applied };
        const thumb = this.thumbs.find(
          (item) => item.dataset.full === this.applied.image?.url
        );
        if (thumb) this.selectImage(thumb, { keepPosition: true });
        if (this.field) this.field.value = this.state.text;
        this.renderLogo();
        const font = this.fontButtons.find(
          (button) =>
            (button.dataset.personalizerFont || "") ===
            (this.applied.font?.family || "")
        );
        if (font) this.selectFont(font);
      }

      this.classList.remove("hidden");
      this.classList.add("flex");
      this.setAttribute("aria-hidden", "false");
      this.openButtons.forEach((button) => button.setAttribute("aria-expanded", "true"));

      if (this.overlay) {
        requestAnimationFrame(() => {
          this.overlay.classList.add("opacity-100");
          this.overlay.classList.remove("opacity-0");
        });
      }

      document.documentElement.classList.add("overflow-hidden");

      if (!this._focusTrap) {
        this._focusTrap = webvista.createFocusTrap(this, {
          onEscape: () => this.onClose(),
        });
      }
      this.addEventListener("keydown", this._focusTrap);

      requestAnimationFrame(() => {
        this.renderText();
        // Always show the popup from the top: focusing the input would otherwise
        // scroll the panel down on short windows.
        const panel = this.querySelector(".product-personalizer__panel");
        if (panel) panel.scrollTop = 0;
        this.field?.focus({ preventScroll: true });
        if (panel) panel.scrollTop = 0;
      });
    }

    onClose(event) {
      if (event) event.preventDefault();
      if (this.overlay) {
        this.overlay.classList.add("opacity-0");
        this.overlay.classList.remove("opacity-100");
      }
      this.classList.add("hidden");
      this.classList.remove("flex");
      this.setAttribute("aria-hidden", "true");
      this.openButtons.forEach((button) => button.setAttribute("aria-expanded", "false"));

      if (this._focusTrap) this.removeEventListener("keydown", this._focusTrap);
      document.documentElement.classList.remove("overflow-hidden");

      if (this.opener) {
        this.opener.focus();
        this.opener = null;
      }
    }

    /* --------------------------------------------------------------- selection */

    thumbToImage(thumb) {
      return {
        url: thumb.dataset.full || "",
        alt: thumb.dataset.alt || "",
        label: thumb.dataset.label || "",
        width: Number.parseInt(thumb.dataset.width || "0", 10),
        height: Number.parseInt(thumb.dataset.height || "0", 10),
      };
    }

    selectImage(thumb, { keepPosition = false } = {}) {
      if (!thumb) return;
      const image = this.thumbToImage(thumb);
      this.state.image = image;

      this.thumbs.forEach((item) => {
        const selected = item === thumb;
        item.classList.toggle("is-selected", selected);
        item.setAttribute("aria-pressed", selected ? "true" : "false");
      });

      if (this.image) {
        this.image.src = image.url;
        this.image.alt = image.alt;
      }
      if (this.stage && image.width && image.height) {
        this.stage.style.aspectRatio = `${image.width} / ${image.height}`;
      }
      if (!keepPosition) {
        this.state.x = 50;
        this.state.y = 50;
      }
      this.renderText();
    }

    /* -------------------------------------------------------------------- logo */

    onLogoChange(event) {
      const file = event.target.files && event.target.files[0];
      this.showLogoError("");
      if (!file) {
        this.clearLogo();
        return;
      }

      // Shopify rejects line item files over 20 MB.
      const maxBytes = 20 * 1024 * 1024;
      if (file.size > maxBytes) {
        this.clearLogo();
        this.showLogoError(
          this.dataset.logoTooLarge || "El archivo supera los 20 MB."
        );
        return;
      }
      if (!/^image\//.test(file.type)) {
        this.clearLogo();
        this.showLogoError(
          this.dataset.logoWrongType || "Sube una imagen (PNG, JPG, WEBP o SVG)."
        );
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        this.state.logo = { name: file.name, dataUrl: String(reader.result) };
        this.renderLogo();
        this.renderText();
      };
      reader.onerror = () => {
        this.clearLogo();
        this.showLogoError("No se pudo leer el archivo.");
      };
      reader.readAsDataURL(file);
    }

    onLogoRemove(event) {
      if (event) event.preventDefault();
      this.clearLogo();
      this.renderText();
    }

    onLogoSizeChange(event) {
      this.state.logoSize = Number.parseInt(event.target.value, 10) || 30;
      this.renderLogo();
      this.renderText();
    }

    clearLogo() {
      this.state.logo = null;
      if (this.logoInput) this.logoInput.value = "";
      this.renderLogo();
    }

    showLogoError(message) {
      if (!this.logoError) return;
      this.logoError.textContent = message;
      this.logoError.classList.toggle("hidden", !message);
    }

    renderLogo() {
      const logo = this.state.logo;
      if (this.logoPreview) {
        if (logo) {
          this.logoPreview.src = logo.dataUrl;
          this.logoPreview.classList.remove("hidden");
          this.applyLogoSize();
        } else {
          this.logoPreview.removeAttribute("src");
          this.logoPreview.classList.add("hidden");
          this.logoPreview.style.removeProperty("width");
        }
      }
      if (this.logoName) this.logoName.textContent = logo ? logo.name : "";
      this.logoRemove?.classList.toggle("hidden", !logo);
      this.logoSizeField?.classList.toggle("hidden", !logo);
      if (this.logoSizeInput) this.logoSizeInput.value = String(this.state.logoSize);
      if (this.logoSizeValue) {
        this.logoSizeValue.textContent = `${this.state.logoSize}%`;
      }
    }

    selectFont(button) {
      if (!button) return;
      this.state.font = {
        name: button.dataset.fontName || "",
        family: button.dataset.personalizerFont || "",
      };
      this.fontButtons.forEach((item) => {
        const selected = item === button;
        item.classList.toggle("is-selected", selected);
        item.setAttribute("aria-pressed", selected ? "true" : "false");
      });
      this.renderText();
    }

    onTextInput(event) {
      this.state.text = event.target.value;
      this.renderText();
    }

    // The logo is sized against the stage, never against its own (draggable)
    // container: a percentage there would grow and shrink while dragging.
    applyLogoSize() {
      if (!this.logoPreview || !this.state.logo) return;
      const stageWidth = this.stage?.clientWidth || 0;
      if (!stageWidth) return;
      const width = Math.round((stageWidth * this.state.logoSize) / 100);
      this.logoPreview.style.width = `${width}px`;
    }

    /* ----------------------------------------------------------------- preview */

    renderText() {
      if (!this.textLayer) return;
      const text = this.state.text.trim();
      const hasLogo = Boolean(this.state.logo);

      // Only the text node is rewritten: the logo <img> lives in the same
      // draggable layer and must survive every keystroke.
      if (this.textValue) this.textValue.textContent = this.state.text;
      this.applyLogoSize();
      this.textLayer.classList.toggle("is-empty", text === "" && !hasLogo);
      this.textLayer.style.color = this.state.color;
      this.textLayer.style.fontFamily = this.state.font?.family || "";

      // Font size follows the stage width so the preview scales with the layout.
      const stageWidth = this.stage?.clientWidth || 0;
      if (stageWidth) {
        this.textLayer.style.fontSize = `${Math.max(12, stageWidth * 0.07)}px`;
      }

      this.clampPosition();
      this.textLayer.style.left = `${this.state.x}%`;
      this.textLayer.style.top = `${this.state.y}%`;

      if (this.counter) {
        this.counter.textContent = `${this.state.text.length}/${this.maxCharacters}`;
      }
      // Either a text or a logo is enough to personalize.
      if (this.applyButton) this.applyButton.disabled = text === "" && !hasLogo;
    }

    // Keep the whole text box inside the image area.
    clampPosition() {
      if (!this.stage || !this.textLayer) return;
      const stageRect = this.stage.getBoundingClientRect();
      if (!stageRect.width || !stageRect.height) return;
      const textRect = this.textLayer.getBoundingClientRect();
      const halfX = (textRect.width / 2 / stageRect.width) * 100;
      const halfY = (textRect.height / 2 / stageRect.height) * 100;
      this.state.x = Math.min(100 - halfX, Math.max(halfX, this.state.x));
      this.state.y = Math.min(100 - halfY, Math.max(halfY, this.state.y));
    }

    onResize() {
      if (this.getAttribute("aria-hidden") === "false") this.renderText();
    }

    /* --------------------------------------------------------------- dragging */

    onPointerDown(event) {
      if (!this.stage) return;
      event.preventDefault();
      this.dragging = true;
      this.textLayer.classList.add("is-dragging");
      this.textLayer.setPointerCapture?.(event.pointerId);
      document.addEventListener("pointermove", this.onPointerMove);
      document.addEventListener("pointerup", this.onPointerUp);
    }

    onPointerMove(event) {
      if (!this.dragging || !this.stage) return;
      const rect = this.stage.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      this.state.x = ((event.clientX - rect.left) / rect.width) * 100;
      this.state.y = ((event.clientY - rect.top) / rect.height) * 100;
      this.renderText();
    }

    onPointerUp() {
      if (!this.dragging) return;
      this.dragging = false;
      this.textLayer.classList.remove("is-dragging");
      document.removeEventListener("pointermove", this.onPointerMove);
      document.removeEventListener("pointerup", this.onPointerUp);
    }

    onTextKeydown(event) {
      const step = event.shiftKey ? 5 : 1;
      const moves = {
        ArrowLeft: [-step, 0],
        ArrowRight: [step, 0],
        ArrowUp: [0, -step],
        ArrowDown: [0, step],
      };
      const move = moves[event.key];
      if (!move) return;
      event.preventDefault();
      this.state.x += move[0];
      this.state.y += move[1];
      this.renderText();
    }

    /* ------------------------------------------------------------------ commit */

    onApply(event) {
      event.preventDefault();
      if (!this.state.text.trim() && !this.state.logo) return;

      this.applied = { ...this.state };
      this.writeProperties(this.applied);
      this.renderSummary();
      this.onClose();
    }

    onRemove(event) {
      event.preventDefault();
      this.applied = null;
      this.state = {
        text: "",
        color: this.dataset.defaultColor || "#000000",
        image: this.thumbs[0] ? this.thumbToImage(this.thumbs[0]) : null,
        font: {
          name: this.fontButtons[0]?.dataset.fontName || "",
          family: this.fontButtons[0]?.dataset.personalizerFont || "",
        },
        logo: null,
        logoSize: 30,
        x: 50,
        y: 50,
      };
      if (this.fontButtons[0]) this.selectFont(this.fontButtons[0]);
      if (this.field) this.field.value = "";
      if (this.thumbs[0]) this.selectImage(this.thumbs[0]);
      this.clearLogo();
      this.showLogoError("");
      this.writeProperties(null);
      this.renderText();
      this.renderSummary();
    }

    writeProperties(data) {
      const set = (key, value) => {
        const input = this.querySelector(`[data-personalizer-input="${key}"]`);
        if (!input) return;
        input.value = value || "";
        input.disabled = !data || !value;
      };
      set("text", data?.text);
      set("image", data?.image?.label || data?.image?.alt);
      // The font only matters when there is text to engrave.
      set("font", data?.text ? data?.font?.name : "");
      set("logo_size", data?.logo ? `${data.logoSize}%` : "");
      set(
        "position",
        data
          ? `${this.dataset.placementName || "Pecho"} · x ${data.x.toFixed(
              0
            )}% y ${data.y.toFixed(0)}%`
          : ""
      );
      set("image_url", data?.image?.url);

      // The file input is the one the customer picked from, so Shopify uploads
      // it with the line. Disable it when there is no logo to send.
      if (this.logoInput) {
        const hasFile = Boolean(data && data.logo && this.logoInput.files?.length);
        this.logoInput.disabled = !hasFile;
      }
    }

    renderSummary() {
      document.querySelectorAll("[data-personalizer-summary]").forEach((summary) => {
        if (!this.triggerZoneOf(summary)) return;
        const textNode = summary.querySelector("[data-personalizer-summary-text]");
        const swatch = summary.querySelector("[data-personalizer-summary-swatch]");
        const hasData = Boolean(this.applied);
        summary.hidden = !hasData;
        summary.classList.toggle("hidden", !hasData);
        if (hasData) {
          const parts = [];
          if (this.applied.text.trim()) parts.push(`“${this.applied.text}”`);
          if (this.applied.logo) parts.push(this.applied.logo.name);
          if (textNode) textNode.textContent = parts.join(" + ");
          if (swatch) swatch.style.background = this.applied.color;
        }
      });
    }
  }

  customElements.define("product-personalizer", ProductPersonalizer);
}
